import {Component, h} from 'preact';
import cx from 'classnames';
import invoke from 'lodash.invoke';
import Provider from 'preact-context-provider';
import List from './List';
import Item from './Item';
import Selection from './Selection';
import EditAction from './EditAction';

export function withPickList ( Component ) {
    function Wrapper ( props, context ) {
        return (
            <Component pickList={{...context}} {...props} />
        );
    }

    Wrapper.displayName = `withPickList(${Component.displayName || Component.name})`;
    return Wrapper;
}

class PickList extends Component {

    static defaultProps = {
        list: [],
        itemIdProp: 'id',
        itemType: 'item',
        groupId: 'picklist',
        allowAddItem: false,
        defaultSelection: null
    };

    static List = List;
    static Item = Item;
    static Selection = Selection;
    static EditAction = EditAction;

    constructor ( props ) {
        super( props );

        this.handleItemChange = this.handleItemChange.bind( this );
        this.handleItemAdded = this.handleItemAdded.bind( this );
        this.toggleEditing = this.toggleEditing.bind( this );
        this.toggleAddItem = this.toggleAddItem.bind( this );
        this.getToggleButtonLabel = this.getToggleButtonLabel.bind( this );
        this.isEditable = this.isEditable.bind( this );



        this.state = {
            allowAddItem: props.allowAddItem,
            selectedItem: this.getSelectedItem( props ),
            isEditing: false,
            isAddingItem: false
        };
    }

    getSelectedItem({defaultSelection, list}) {
        // if we're dealing with a single item list this item should always be the selected item
        return (list.length === 1) ? list[0] : defaultSelection;
    }

    componentWillMount () {
        this.renderChildren = (typeof this.props.children[0] === 'function')
            ? this.props.children[0]
            : function () {};
    }

    componentDidMount () {
        const {selectedItem} = this.state;
        if ( selectedItem != null ) {
            invoke( this.props, 'onItemChange', selectedItem, this.props.groupId );
        }
    }

    componentDidUpdate ( previousProps, previousState, previousContext ) {
        const {list, itemIdProp, defaultSelection} = this.props;

        if ( !previousProps.defaultSelection || !defaultSelection ) {
            return;
        }

        if ( previousProps.defaultSelection[itemIdProp] !== defaultSelection[itemIdProp] ) {
            const selectedItem = list.find( item => defaultSelection[itemIdProp] == item[itemIdProp] );
            this.setState( {selectedItem} );
        }
    }

    isEditable () {
        return this.props.list.length > 1 || this.state.allowAddItem;
    }

    handleItemChange ( id ) {
        const {list, itemIdProp, groupId} = this.props;
        const selectedItem = list.find( item => id == item[itemIdProp] );

        this.toggleEditing();
        this.setState( {selectedItem} );
        invoke( this.props, 'onItemChange', selectedItem, groupId );
    }

    handleItemAdded ( newItem, ...rest ) {
        this.setState( {
            selectedItem: newItem,
            isEditing: false
        } );
        invoke( this.props, 'onItemAdded', newItem, ...rest );
    }

    toggleEditing () {
        this.setState( {
            isEditing: !this.state.isEditing,
            isAddingItem: false
        } );

        // return scroll to selected item
        if ( this.container ) {
            this.container.focus();
        }
    }

    toggleAddItem () {
        this.setState( {isAddingItem: !this.state.isAddingItem} )
    }

    getToggleButtonLabel () {
        const {isEditing, selectedItem} = this.state;

        if ( isEditing ) {
            return 'Cancel change';
        }
        if ( !selectedItem ) {
            return `Select ${this.props.itemType}`;
        }

        return 'Change';
    }

    getContext () {
        const {list, itemIdProp, itemType, groupId} = this.props;
        return {
            ...this.state,
            list, itemIdProp, itemType, groupId,
            getToggleButtonLabel: this.getToggleButtonLabel,
            isEditable: this.isEditable,
            actions: {
                handleItemChange: this.handleItemChange,
                handleItemAdded: this.handleItemAdded,
                toggleEditing: this.toggleEditing,
                toggleAddItem: this.toggleAddItem
            }
        }
    }

    render ( {className} ) {
        const pickList = this.getContext();
        const containerClasses = cx( className, 'relative' );

        return (
            <Provider {...pickList}>
                <div className={containerClasses} tabIndex="-1" ref={ref => this.container = ref}>
                    {this.renderChildren( pickList )}
                </div>
            </Provider>
        );
    }
}

export default PickList;